import type {
  GridApi,
  GridCellParams,
  GridCellSelectionModel,
  GridColDef,
  GridRowSelectionModel,
  GridSingleSelectColDef,
} from '@repo/go-table';
import { getUnixTime, isDate } from 'date-fns';
import i18nextInstance from 'i18next';
import { action, computed, makeObservable, observable } from 'mobx';
import type { MutableRefObject } from 'react';

import type { BaseParameterTypes } from '../../shared/baseParams';
import { depthSortedParameters, nameDictionary, timeSortedParametersNoAutoRecognized } from '../../shared/baseParams';
import { convertExcelStringToValueArrays } from '../../shared/convertExcelStringToValueArrays';
import type { INotificationsService } from '../../shared/INotificationsService';
import type { TAlertRule, TSourceType } from '../types';

import type { TData, AlertRule } from './services/AlertRule.entity';
import type { AlertRulesService } from './services/AlertRulesService';
import { DisplayManager, SERVICE_COLUMNS } from './services/DisplayManager';

type SingleSelectOption =
  | string
  | number
  | { label?: unknown; sourceType?: TSourceType; value?: unknown; [key: string]: unknown };

const CLEARABLE_FIELDS = new Set<keyof TAlertRule>([
  'riskLevel',
  'anomalyTypeId',
  'anomalySubtypeId',
  'sourceType',
  'sourceOption',
  'holeDepthFrom',
  'holeDepthTo',
  'depthFrom',
  'depthTo',
  'unitId',
  'minValue',
  'minGoStreamCurveId',
  'minPercent',
  'maxValue',
  'maxGoStreamCurveId',
  'maxPercent',
  'diffValue',
  'diffInterval',
]);

export class AlertRulesTableStore {
  private tableApi: MutableRefObject<GridApi>;
  @observable private isMainListenersInitialized = false;

  @observable displayManager: DisplayManager;
  @observable selectedRows: GridRowSelectionModel = [];

  @observable isTableSettingsOpen = false;

  @observable isContextPanelOpen = false;

  @observable isRowSelectionModeOn = false;

  constructor(
    tableApi: MutableRefObject<GridApi>,
    private readonly notifications: INotificationsService,
    private readonly alertRulesService: AlertRulesService,
  ) {
    this.tableApi = tableApi;

    this.displayManager = new DisplayManager(
      this.alertRulesService.settingsStore,
      this.alertRulesService.index,
      this.alertRulesService.uiState,
    );

    makeObservable(this);
  }

  @computed
  get isInitialized(): boolean {
    return this.isMainListenersInitialized && !!this.displayManager;
  }

  @computed
  get isLoading(): boolean {
    return this.alertRulesService.loading;
  }

  get isEditing(): boolean {
    return this.alertRulesService.uiState.editing;
  }

  @computed
  get isRowSelectingEnabled(): boolean {
    return this.isEditing || this.isRowSelectionModeOn;
  }

  @action.bound
  private addAlertRules(rules: AlertRule[]): void {
    if (!this.tableApi || !rules.length) {
      return;
    }

    this.tableApi.current.updateRows(rules);

    const lastRule = rules[rules.length - 1];

    if (lastRule) {
      setTimeout(() => {
        this.tableApi?.current.setCellFocus(lastRule.entityId, 'anomalyTypeId');
        this.tableApi?.current.startCellEditMode({ id: lastRule.entityId, field: 'anomalyTypeId' });
      }, 100);
    }
  }

  @action.bound
  private updateAlertRules(rules: AlertRule[]): void {
    if (!this.tableApi || !rules.length) {
      return;
    }

    this.tableApi.current.updateRows(rules);
  }

  @action.bound
  private deleteAlertRules(ids: string[]): void {
    if (!this.tableApi || !ids.length) {
      return;
    }

    this.tableApi.current.updateRows(ids.map((id) => ({ entityId: id, _action: 'delete' })));
  }

  getFieldValue = <T extends keyof TAlertRule>(rowId: string, field: T): TAlertRule[T] | null => {
    const record = this.alertRulesService.alertRulesMap.get(rowId);

    const fieldValue = record?.data[field] ?? null;

    if (fieldValue == null) {
      return null;
    }

    return fieldValue as TAlertRule[T];
  };

  @action.bound
  onAlertRuleItemValueChange<T extends keyof TAlertRule>(
    rowId: string,
    field: T,
    value: TAlertRule[T] | undefined,
  ): void {
    this.alertRulesService.onAlertRuleItemValueChange(rowId, field, value);
  }

  @action.bound
  setSelectedRows(rowSelectModel: GridRowSelectionModel): void {
    this.selectedRows = rowSelectModel;
    this.tableApi.current.setRowSelectionModel(rowSelectModel);
  }

  isCellEditable(params: GridCellParams<AlertRule>): boolean {
    if (!params.colDef.editable) {
      return false;
    }

    const rowId = params.row.entityId;
    const sourceType = this.getFieldValue(rowId, 'sourceType');

    switch (params.field) {
      case 'sectionTypeIds':
        return false;
      case 'minValue':
        return this.getFieldValue(rowId, 'minGoStreamCurveId') == null;
      case 'minGoStreamCurveId':
        return (
          this.getFieldValue(rowId, 'minValue') == null &&
          sourceType !== 'AUTOSUPERVISOR_KPI_STATES' &&
          sourceType !== 'AUTOSUPERVISOR_OPERATIONS'
        );
      case 'minPercent':
        return this.getFieldValue(rowId, 'maxGoStreamCurveId') == null;
      case 'maxValue':
        return this.getFieldValue(rowId, 'maxGoStreamCurveId') == null;
      case 'maxGoStreamCurveId':
        return (
          this.getFieldValue(rowId, 'maxValue') == null &&
          sourceType !== 'AUTOSUPERVISOR_KPI_STATES' &&
          sourceType !== 'AUTOSUPERVISOR_OPERATIONS'
        );
      default:
        return true;
    }
  }

  private isCellClearable(rowId: string, field: string): field is keyof TAlertRule {
    const row = this.alertRulesService.alertRulesMap.get(rowId);

    if (!row || !CLEARABLE_FIELDS.has(field as keyof TAlertRule)) {
      return false;
    }

    const column = this.tableApi.current.getColumn(field);

    if (!column) {
      return false;
    }

    return this.isCellEditable({ id: rowId, field, row, colDef: column } as GridCellParams<AlertRule>);
  }

  @action.bound
  handleDelete(params: GridCellParams<AlertRule>): void {
    if (!this.isEditing) {
      return;
    }

    const cellSelection = this.tableApi.current.getCellSelectionModel();
    const targetCells =
      cellSelection && Object.keys(cellSelection).length
        ? cellSelection
        : {
            [params.id]: {
              [params.field]: true,
            },
          };
    const patches: [string, Partial<TData>][] = [];

    for (const rowId in targetCells) {
      const patch: Partial<TData> = {};

      for (const field in targetCells[rowId]) {
        if (!targetCells[rowId][field]) {
          continue;
        }

        if (this.isCellClearable(rowId, field)) {
          patch[field] = undefined;
        }
      }

      if (Object.keys(patch).length) {
        patches.push([rowId, patch]);
      }
    }

    if (patches.length) {
      this.alertRulesService.applyAlertRuleItemPatches(patches);
    }
  }

  private getAnomalySubtypeOptions() {
    return this.alertRulesService.anomalySubtypes.map((anomalySubtype) => ({
      label: anomalySubtype.data['name']?.toString() ?? '',
      value: anomalySubtype.id as number,
    }));
  }

  private getSourceOptionOptions(): SingleSelectOption[] {
    const baseParameters =
      this.alertRulesService.index === 'time' ? timeSortedParametersNoAutoRecognized : depthSortedParameters;
    const generalParameterOptions = baseParameters.map((parameter: BaseParameterTypes) => ({
      label: i18nextInstance.t(`alertsManagerWidget:baseParameters.${nameDictionary[parameter]}`),
      value: parameter,
      sourceType: 'GENERAL_PARAMETERS' as const,
    }));

    if (this.alertRulesService.index !== 'time') {
      return generalParameterOptions;
    }

    return [
      ...generalParameterOptions,
      ...this.alertRulesService.autosupervisorOperations.map((operation) => ({
        label: operation.data['name']?.toString() ?? '',
        value: operation.data.code as number,
        sourceType: 'AUTOSUPERVISOR_OPERATIONS' as const,
      })),
      ...this.alertRulesService.autosupervisorKPIstates.map((kpi) => ({
        label: kpi.data['name']?.toString() ?? '',
        value: kpi.data.code as number,
        sourceType: 'AUTOSUPERVISOR_KPI_STATES' as const,
      })),
    ];
  }

  private findSingleSelectOption(options: SingleSelectOption[], columnValue: string | number | boolean | Date) {
    const normalizedValue = columnValue.toString().toLowerCase();
    return options.find((option) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return option.toString().toLowerCase() === normalizedValue;
      }

      return option.label?.toString().toLowerCase() === normalizedValue;
    });
  }

  private parsePasteDataToAlertRuleData(
    columns: GridColDef[],
    rows: (string | number | boolean | Date | null)[][],
    existingRowIds: string[] = [],
  ): TData[] {
    const data = columns.reduce((acc: TData[], col, columnIndex) => {
      if (col.field === 'sectionTypeIds') {
        return acc;
      }

      rows.forEach((row, rowIndex) => {
        if (columnIndex >= row.length) {
          return;
        }

        const data = { ...(acc[rowIndex] ?? {}) };
        const existingRowId = existingRowIds[rowIndex];
        const existingRule = existingRowId ? this.alertRulesService.alertRulesMap.get(existingRowId) : null;
        const rowData = { ...(existingRule?.data ?? {}), ...data };

        const columnValue = row[columnIndex];

        if (columnValue === null) {
          data[col.field] = null;
        } else {
          switch (col.type) {
            case 'singleSelect': {
              const optionsRaw = (col as GridSingleSelectColDef).valueOptions;

              let options: SingleSelectOption[] = (() => {
                if (Array.isArray(optionsRaw)) {
                  return optionsRaw;
                }

                return optionsRaw?.({ field: col.field, row: { data: rowData } }) ?? [];
              })();

              if (col.field === 'anomalySubtypeId' && !options.length) {
                options = this.getAnomalySubtypeOptions();
              }

              if (col.field === 'sourceOption' && !options.length) {
                options = this.getSourceOptionOptions();
              }

              const value = this.findSingleSelectOption(options, columnValue);

              if (value != null) {
                if (typeof value === 'string' || typeof value === 'number') {
                  data[col.field] = value;
                } else if (typeof value.value === 'string' || typeof value.value === 'number') {
                  data[col.field] = value.value;
                  if (col.field === 'sourceOption' && value.sourceType != null) {
                    data.sourceType = value.sourceType;
                  }
                }
              }

              break;
            }

            case 'date':
            case 'dateTime': {
              if (isDate(columnValue)) {
                data[col.field] = getUnixTime(columnValue as Date);
              } else {
                console.warn(`given value for column ${col.field} is not date`, columnValue);
              }

              break;
            }

            case 'string': {
              if (typeof columnValue === 'string') {
                data[col.field] = columnValue;
              } else {
                data[col.field] = columnValue.toString();
              }

              break;
            }

            case 'boolean': {
              if (typeof columnValue === 'boolean') {
                data[col.field] = columnValue;
              } else {
                data[col.field] = !!columnValue;
              }

              break;
            }

            case 'number': {
              if (typeof columnValue === 'number') {
                data[col.field] = columnValue;
                break;
              }

              if (columnValue == null) {
                data[col.field] = null;
                break;
              }

              const parsedValue = Number(columnValue);

              if (!Number.isNaN(parsedValue)) {
                data[col.field] = parsedValue;
              } else {
                data[col.field] = null;
              }

              break;
            }

            default: {
              data[col.field] = null;
            }
          }
        }

        acc[rowIndex] = data;
      });

      return acc;
    }, []);

    return data;
  }

  private copySelectedRowsToClipboard = async () => {
    const api = this.tableApi?.current;

    if (!api) {
      return;
    }

    const rowsVisibility = api.state.filter.filteredRowsLookup;
    const columnsVisibility = api.state.columns.columnVisibilityModel;

    const sortedRows = api.getSortedRows();
    const selectedRows = api.getSelectedRows();
    const filteredRows = sortedRows.filter((row) => selectedRows.get(row.entityId) && rowsVisibility[row.entityId]);
    const columns = api.getAllColumns();

    const copyData = filteredRows
      .map((row) => {
        const values: string[] = [];

        for (const { field } of columns) {
          if (columnsVisibility[field] === false || SERVICE_COLUMNS.includes(field) || field === '__check__') {
            continue;
          }

          const cellParams = api.getCellParams(row.entityId, field);

          if (!cellParams) {
            console.warn(`cannot find corresponding cellParams wit rowId ${row.id} and fieldId ${field}`);

            values.push('-');
            continue;
          }

          values.push(cellParams.formattedValue?.toString() ?? cellParams.value?.toString() ?? '-');
        }

        return values.join('\t');
      })
      .join('\n');

    try {
      await navigator.clipboard.writeText(copyData);

      this.setSelectedRows([]);
      this.tableApi.current.setRowSelectionModel([]);
      this.notifications.showSuccessMessageT('alertsManagerWidget:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsManagerWidget:errors.blockedCopyPaste');
    }
  };

  private copySelectedCellData = async (selectedCellData: GridCellSelectionModel) => {
    const api = this.tableApi?.current;

    if (!api) {
      return;
    }

    const dataRows: string[] = [];

    for (const rowId in selectedCellData) {
      const values: string[] = [];

      for (const fieldId in selectedCellData[rowId]) {
        const cellParams = api.getCellParams(rowId, fieldId);

        if (!cellParams) {
          console.warn(`cannot find corresponding cellParams wit rowId ${rowId} and fieldId ${fieldId}`);

          values.push('-');
          continue;
        }

        values.push(cellParams.formattedValue?.toString() ?? cellParams.value?.toString() ?? '-');
      }

      dataRows.push(values.join('\t'));
    }

    const copyData = dataRows.join('\n');

    try {
      await navigator.clipboard.writeText(copyData);

      api.setRowSelectionModel([]);
      this.notifications.showSuccessMessageT('alertsManagerWidget:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsManagerWidget:errors.blockedCopyPaste');
    }
  };

  @action.bound
  handleCopy() {
    const api = this.tableApi;

    if (!api) {
      return;
    }

    const selectedRows = api.current.getSelectedRows();

    if (selectedRows.size < 1) {
      const cellSelection = api.current.getCellSelectionModel();
      const focusedCellData = api.current.state.focus.cell;

      const tableCopyTargetData = ((): GridCellSelectionModel | null => {
        if (cellSelection && !!Object.keys(cellSelection).length) {
          return cellSelection;
        }

        if (focusedCellData) {
          return {
            [focusedCellData.id]: {
              [focusedCellData.field]: true,
            },
          };
        }

        return null;
      })();

      if (!tableCopyTargetData) {
        return;
      }

      this.copySelectedCellData(tableCopyTargetData);

      return;
    }

    this.copySelectedRowsToClipboard();
  }

  @action.bound
  async handlePaste(): Promise<void> {
    const api = this.tableApi?.current;

    if (!api || !this.isEditing) {
      return;
    }

    const focusedCellData = api.state.focus.cell;

    let clipboardData = '';

    try {
      clipboardData = await navigator.clipboard.readText();
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsManagerWidget:errors.blockedCopyPaste');
      return;
    }

    if (!clipboardData || typeof clipboardData !== 'string') {
      return;
    }

    const columns = api
      .getVisibleColumns()
      .filter((c) => !SERVICE_COLUMNS.includes(c.field) && c.field !== '__check__');
    const initialDataRows = convertExcelStringToValueArrays(clipboardData, {
      boolean: {
        true: [i18nextInstance.t('common:yes')],
        false: [i18nextInstance.t('common:no')],
      },
    }).filter((row) => !row.every((val) => val === null));

    if (!focusedCellData) {
      const alertRulesData = this.parsePasteDataToAlertRuleData(columns, initialDataRows);
      this.alertRulesService.addNewAlertRuleItemPatches(alertRulesData);
      return;
    }

    const pasteTargetColumnIndex = columns.findIndex((c) => c.field === focusedCellData.field);
    const existingRows = (() => {
      const allRowsIds = api.getSortedRowIds();
      const index = allRowsIds.indexOf(focusedCellData.id);

      return allRowsIds.slice(index, index + initialDataRows.length);
    })();
    const ruleData = this.parsePasteDataToAlertRuleData(
      columns.slice(pasteTargetColumnIndex),
      initialDataRows,
      existingRows.map(String),
    );

    const updateRuleData = ruleData.slice(0, existingRows.length);
    const newRuleData = ruleData.slice(existingRows.length);

    this.alertRulesService.applyAlertRuleItemPatches(
      existingRows.map((rowId, index) => [rowId as string, updateRuleData[index]]),
    );

    this.alertRulesService.addNewAlertRuleItemPatches(newRuleData);
  }

  @action.bound
  setIsTableSettingsOpen(is: boolean): void {
    this.isTableSettingsOpen = is;
  }

  @action.bound
  setIsContextPanelOpen(is: boolean): void {
    this.isContextPanelOpen = is;
  }

  @action.bound
  setIsRowSelectionModeOn(is: boolean): void {
    this.isRowSelectionModeOn = is;
  }

  @action.bound
  init() {
    const newOperationsDisposer = this.alertRulesService.alertRulesEventEmitter.addEventListener(
      'newAlertRules',
      this.addAlertRules,
    );

    const updatedOperationsDisposer = this.alertRulesService.alertRulesEventEmitter.addEventListener(
      'updateAlertRules',
      this.updateAlertRules,
    );

    const deletedOperationsDisposer = this.alertRulesService.alertRulesEventEmitter.addEventListener(
      'deleteAlertRules',
      this.deleteAlertRules,
    );

    this.isMainListenersInitialized = true;

    return () => {
      newOperationsDisposer();
      updatedOperationsDisposer();
      deletedOperationsDisposer();
    };
  }
}
